import dayjs from 'dayjs';
import { pick, keys, omit } from 'lodash';

import {
  jsUcOnlyFirst,
  parseNumber,
  removeEmpty,
  transformIncluded,
} from 'modules/common/utils';

const productKey = {
  id: '',
  code: '',
  name: '',
  photo_url: '',
  product_uuid: '',
  price: '0.00',
  quantity: 0,
  description: '',
  addOns: [],
  variants: {},
};

const logsKey = {
  id: '',
  status: '',
  description: '',
  created_at: '',
  updated_at: '',
  uuid: '',
  vendor_order_uuid: '',
};

const orderKey = {
  id: '',
  tax: '',
  waybill_url: '',
  uuid: '',
  rider: '',
  total: '',
  status: '',
  branch: {},
  ratings: [],
  remarks: '',
  subtotal: '',
  discount: '',
  products: [],
  orderLogs: [],
  first_name: '',
  last_name: '',
  created_at: '',
  updated_at: '',
  branch_name: '',
  branch_uuid: '',
  vendor_uuid: '',
  prepared_at: '',
  delivered_at: '',
  scheduled_at: '',
  scheduled_delivery_date: '',
  delivery_fee: '',
  delivery_courier_label: '',
  delivery_courier_partner_id: '',
  order_number: '',
  merchant_name: '',
  delivery_type: '',
  customer_name: '',
  system_status: '',
  schedule_type: '',
  is_rebooking: '',
  tracking_url: '',
  transaction_type: '',
  scheduled_time: '',
  payment_method: '',
  payment_method_label: '',
  customer_address: '',
  rider_confirmed_at: '',
  branch_mobile_number: '',
  is_ready_for_booking: '',
  promo: {},
  original_total: '',
  is_customer_pickup: '',
  is_in_house: '',
  rebooking_couriers: '',
  customer_contact_number: '',
  customer_address_remarks: '',
  customer_email_address: '',
  branch_pinned_address: '',
};

export const transformProduct = (data, included) => {
  const $data = transformIncluded(data, included);
  const { addOns, branch } = $data?.included;

  const args = {
    ...$data?.attributes,
    branch_name: branch?.attributes?.branch_name,

    addOns:
      addOns?.map((item) => ({
        ...omit(item, ['attributes', 'links']),
        ...item?.attributes,
        id: item?.id,
      })) || [],
    id: $data?.id,
  };

  return { ...pick(args, keys(productKey)) };
};

export const transformOrderLog = (data) => {
  const args = {
    ...data,
    ...data?.attributes,
    id: data?.id,
  };

  return { ...pick(args, keys(logsKey)) };
};

export const transformOrder = (data, included = {}) => {
  try {
    const $data = transformIncluded(data, included);

    const {
      vendor,
      products,
      customer,
      orderLogs,
      branch_user,
      thirdPartyBooking,
    } = $data?.included || {};
    const paymentMethod = included?.find(
      (item) => item?.type === 'payment_channels'
    );

    const selectedCourierId = thirdPartyBooking?.attributes?.partner_id;
    const deliveryCouriers =
      thirdPartyBooking?.attributes?.meta_data?.delivery_couriers || [];
    const selectedCourier = deliveryCouriers?.find(
      (item) => item?.partner_id === selectedCourierId
    );
    const mobile_number =
      branch_user?.attributes?.mobile_number ||
      vendor?.attributes?.mobile_number;

    const { created_at } = $data?.attributes;

    const args = {
      ...omit($data, ['included', 'attributes']),
      ...data?.attributes,
      id: +data?.id,
      rebooking_couriers: deliveryCouriers.length
        ? deliveryCouriers?.filter((i) => i?.partner_id !== 'IN_HOUSE')
        : [],
      is_rebooking: thirdPartyBooking?.attributes?.system_status === 'REBOOK',
      orderLogs: orderLogs?.map((item) => transformOrderLog(item)) ?? [],
      products: products?.map((item) => transformProduct(item, included)) ?? [],
      customer_email_address: customer?.attributes?.email,
      promo: {
        description: '10% off and free delivery',
        code: 'OLANAP2',
      },
      created_at: dayjs(created_at).format('MMM DD, YYYY hh:mm A'),
      branch_mobile_number: mobile_number,
      tracking_url: thirdPartyBooking?.attributes?.tracking_url,
      payment_method_label: paymentMethod?.attributes?.name || null,
      delivery_courier_label: selectedCourier?.name || null,
      delivery_courier_partner_id: selectedCourier?.partner_id,
      waybill_url: thirdPartyBooking?.attributes?.waybill_url,
      subtotal:
        parseNumber(data?.attributes?.subtotal || 0) +
        parseNumber(data?.attributes?.discount || 0),
      original_total:
        parseNumber(data?.attributes?.total || 0) +
        parseNumber(data?.attributes?.discount || 0),
    };

    return { ...pick(args, keys(orderKey)) };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err, ' here');
    return [];
  }
};

export const transformParams = (data) => {
  const { status, date_range } = data;

  const args = {
    ...omit(data, ['status', 'date_range']),
    [`status[0]`]: status === 'ALL_ORDERS' || !status ? '' : status,
    from: date_range?.from ? dayjs(date_range?.from).format('YYYY-MM-DD') : '',
    to: date_range?.to ? dayjs(date_range?.to).format('YYYY-MM-DD') : '',
  };

  return removeEmpty(args);
};

const placeOrderKey = {
  logo: '',
  name: '',
  txnid: '',
  total: '',
  reference: '',
  sub_total: '',
  system_fee: '',
  total_shipping_fee: '',
  shipping_fee: '',
};

export const transformPlaceOrder = (data, included) => {
  const $data = transformIncluded(data, included);
  const { logo, name } = $data?.included?.partner?.attributes;

  const x = {
    ...omit($data, ['attributes']),
    ...omit($data?.attributes, ['relationships']),
    name,
    logo,
  };

  return { ...pick(x, keys(placeOrderKey)) };
};

export const transformOrderLogs = (data) => {
  const $data = data?.length ? data?.reverse() : [];
  const last = $data[data?.length - 1];

  const renderTitle = (row) => ({
    'draft was created.': 'Order was created.',
    default: jsUcOnlyFirst(
      (row?.description || '')
        ?.replace('vendor', 'merchant')
        ?.replace('vendors', 'merchants')
    ),
  });

  const transformedData = $data?.map((item) => ({
    id: item?.id,
    isActive: item?.status === 'DELIVERED' ? false : last?.id === item?.id,
    title:
      renderTitle(item)?.[(item?.description || '')?.toLowerCase()] ||
      renderTitle(item)?.default,
    description: dayjs(item?.created_at).format('LLL'),
  }));

  return transformedData;
};

export const transformConfirmOrder = (payload) => {
  if (!payload) return {};

  const {
    defaultHour,
    reminder_date,
    reminder_time,
    remind_every,
    reminder_via,
  } = payload || {};

  const formatRemindAt = () => {
    if (defaultHour) {
      return dayjs(`${reminder_date} ${reminder_time}`)
        .subtract(defaultHour, 'hour')
        .format('ll HH:mm:00');
    }

    return dayjs(`${reminder_date} ${reminder_time}`).format('ll HH:mm:00');
  };

  const args = {
    remind_at: reminder_date && reminder_time ? formatRemindAt() : null,
    remind: remind_every?.value || null,
    remind_type: reminder_via?.value,
  };

  return args;
};
